/// <amd-module name="Widgets/VelocityWidget/VelocityWidgetComponents/VelocityViewComponent" />
import * as AnalyticsResources from "Analytics/Common/Resources";
import { extractODataError } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { showActionRequiredDialog } from "Reporting/Lightbox/Lightbox";
import { MessageType } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { getProject } from "Tfs/Widget/Context";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { ActionCreatorBase } from "WidgetCommon/ModernWidgetTypes/ActionCreatorBase";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ISettingsManager } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { ViewComponentBase, ViewComponentProps } from "WidgetCommon/ModernWidgetTypes/ViewComponentBase";
import { WidgetDataManagerBase, WidgetDataManagerOptions } from "WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase";
import { ActionMessageOptions, MessageOptions } from "WidgetCommon/ModernWidgetTypes/WidgetMessageCardFactory";
import * as Resources from "Widgets/VelocityWidget/Resources";
import { VelocityActionCreator } from "./VelocityActionCreator";
import { VelocityConstants } from "./VelocityConstants";
import { VelocityDataManager } from "./VelocityDataManager";
import { VelocitySettings } from "./VelocitySettings";
import VelocitySettingsManager from "./VelocitySettingsManager";

export interface VelocityViewComponentProps extends ViewComponentProps {
    context: IVssPageContext;
}

export default class VelocityViewComponent extends ViewComponentBase<VelocityViewComponentProps, VelocitySettings> {
    public constructor(props: VelocityViewComponentProps) {
        super(props);
    }

    protected createDataManager(options: WidgetDataManagerOptions): WidgetDataManagerBase {
        return new VelocityDataManager(this.props.context, options, this.computeCommandName());
    }

    protected createActionCreator(): ActionCreatorBase {
        return new VelocityActionCreator(
            this.props.context,
            this.dataManager,
            this.store,
            this.actions,
            VelocityConstants.featureName,
            extractODataError
        );
    }

    protected createSettingsManager(): ISettingsManager<VelocitySettings> {
        return new VelocitySettingsManager(this.props.context, this.props.teamContext);
    }

    protected getMessageOptions(): MessageOptions | undefined {
        let options: MessageOptions | undefined;

        switch (this.getStoreState().messageType) {
            case MessageType.AxFaultIn:
                options = {
                    actionText: Resources.VelocityWidget_LearnMore,
                    actionAriaLabel: Resources.VelocityWidget_LearnMore,
                    actionOnClickDelegate: () => window.open(Resources.VelocityWidget_LearnMoreLink, "_blank")
                } as ActionMessageOptions;
                break;
            case MessageType.SetIterationDates:
                const linkToSetIterations = routeUrl(this.context.pageContext, "ms.vss-admin-web.project-admin-hub-route", {
                    project: getProject(this.props.context).name,
                    adminPivot: "work"
                });
                options = {
                    actionText: Resources.VelocityWidget_SetIterationDatesLink,
                    actionAriaLabel: Resources.VelocityWidget_SetIterationDatesAriaLabel,
                    actionOnClickDelegate: () => window.open(linkToSetIterations, "_blank")
                } as ActionMessageOptions;
                break;
            case MessageType.MissingPermissions:
                options = {
                    actionOnClickDelegate: () => {
                        showActionRequiredDialog(
                            this.context.pageContext,
                            AnalyticsResources.PermissionDeniedTitleText,
                            <FormatComponent format={AnalyticsResources.PermissionDeniedDetailText}>
                                <a href={AnalyticsResources.PermissionDeniedDetailLinkAddress}>{AnalyticsResources.PermissionDeniedDetailLinkText}</a>
                            </FormatComponent>
                        );
                    }
                } as ActionMessageOptions;
                break;
            default:
                options = undefined;
                break;
        }
        return options;
    }

    /** Pass custom telemetry about the load event for Standardized Widget Reporting. */
    protected packWidgetLoadedTelemetryData(velocitySettings: VelocitySettings): { [key: string]: any } {
        if (velocitySettings && this.props.teamContext) {
            return {
                WorkItemFilterKey: velocitySettings.workItemTypeFilter.identifier,
                WorkItemFilterValue: velocitySettings.workItemTypeFilter.identifier,

                AggregationKey: AggregationMode[velocitySettings.aggregation.identifier],
                AggregationValue: velocitySettings.aggregation.settings,

                // The desired # in config, not the actual number which get rendered, which may be less.
                NumberOfIterations: velocitySettings.numberOfIterations,

                LateWorkDelayEnabled: velocitySettings.lateWorkDelay !== undefined,
                LateWorkDelay: velocitySettings.lateWorkDelay,

                PlannedWorkDelayEnabled: velocitySettings.plannedWorkDelay !== undefined,
                PlannedWorkDelay: velocitySettings.plannedWorkDelay,

                showResolvedItemsAsCompletedEnabled: velocitySettings.showResolvedItemsAsCompleted
            };
        }

        return {};
    }

    private computeCommandName(): string {
        return this.props.presentationMode === WidgetPresentationMode.embeddedView ? "VelocityReport" : "Velocity";
    }
}
